export const adminV2PaywallPopupBranchSlugs = [
  'featureBlocked',
  'paidTool',
  'paidSetting',
  'batchAccess',
  'uploadSizeLimit',
  'concurrencyLimit',
  'upgradeRequired',
  'creditsInsufficient',
] as const;

export type AdminV2PaywallPopupBranchSlug =
  (typeof adminV2PaywallPopupBranchSlugs)[number];

export const adminV2DefaultPaywallPopupBranchSlug =
  'featureBlocked' satisfies AdminV2PaywallPopupBranchSlug;

export type AdminV2PaywallUpgradeReason =
  | 'paid-feature'
  | 'paid-tool'
  | 'paid-setting'
  | 'batch-access'
  | 'upload-size-limit'
  | 'insufficient-credits'
  | 'concurrency-limit'
  | 'upgrade-intent';

type AdminV2PaywallPopupBranchDefinition = {
  slug: AdminV2PaywallPopupBranchSlug;
  titlePlaceholder: string;
  bodyPlaceholder: string;
  buttonPlaceholder: string;
  v1TitleKey: string;
  v1DescriptionKey: string;
};

export const adminV2PaywallPopupBranchDefinitions: readonly AdminV2PaywallPopupBranchDefinition[] =
  [
    {
      slug: 'featureBlocked',
      titlePlaceholder: 'Finish What You Started',
      bodyPlaceholder: 'This feature is available on paid plans.',
      buttonPlaceholder: 'Upgrade now',
      v1TitleKey: 'featureBlockedTitle',
      v1DescriptionKey: 'featureBlockedDescription',
    },
    {
      slug: 'paidTool',
      titlePlaceholder: 'Create With the Tool You Chose',
      bodyPlaceholder: 'Upgrade to run this premium tool.',
      buttonPlaceholder: 'Upgrade now',
      v1TitleKey: 'paidToolTitle',
      v1DescriptionKey: 'paidToolDescription',
    },
    {
      slug: 'paidSetting',
      titlePlaceholder: 'Keep the Setting You Chose',
      bodyPlaceholder: 'Upgrade to use this setting.',
      buttonPlaceholder: 'Upgrade now',
      v1TitleKey: 'paidSettingTitle',
      v1DescriptionKey: 'paidSettingDescription',
    },
    {
      slug: 'batchAccess',
      titlePlaceholder: 'Process All Your Images at Once',
      bodyPlaceholder: 'Upgrade to process more images at once.',
      buttonPlaceholder: 'Upgrade now',
      v1TitleKey: 'batchAccessTitle',
      v1DescriptionKey: 'batchAccessDescription',
    },
    {
      slug: 'uploadSizeLimit',
      titlePlaceholder: 'Keep Your Full-Size Original',
      bodyPlaceholder: 'Upgrade to upload larger images.',
      buttonPlaceholder: 'Upgrade now',
      v1TitleKey: 'uploadSizeLimitTitle',
      v1DescriptionKey: 'uploadSizeLimitDescription',
    },
    {
      slug: 'concurrencyLimit',
      titlePlaceholder: 'Start Your Next Image Now',
      bodyPlaceholder: 'Upgrade to run more jobs at once.',
      buttonPlaceholder: 'Upgrade now',
      v1TitleKey: 'concurrencyLimitTitle',
      v1DescriptionKey: 'concurrencyLimitDescription',
    },
    {
      slug: 'upgradeRequired',
      titlePlaceholder: 'Create More. Wait Less.',
      bodyPlaceholder: 'Upgrade when you need more power.',
      buttonPlaceholder: 'View plans',
      v1TitleKey: 'upgradeIntentTitle',
      v1DescriptionKey: 'upgradeIntentDescription',
    },
    {
      slug: 'creditsInsufficient',
      titlePlaceholder: 'Your Image Is Ready. Generate It Now.',
      bodyPlaceholder: 'Add credits or upgrade your plan.',
      buttonPlaceholder: 'Add credits',
      v1TitleKey: 'insufficientCreditsTitle',
      v1DescriptionKey: 'insufficientCreditsDescription',
    },
  ] as const;

const upgradeReasonToBranchSlug = {
  'paid-feature': 'featureBlocked',
  'paid-tool': 'paidTool',
  'paid-setting': 'paidSetting',
  'batch-access': 'batchAccess',
  'upload-size-limit': 'uploadSizeLimit',
  'insufficient-credits': 'creditsInsufficient',
  'concurrency-limit': 'concurrencyLimit',
  'upgrade-intent': 'upgradeRequired',
} as const satisfies Record<
  AdminV2PaywallUpgradeReason,
  AdminV2PaywallPopupBranchSlug
>;

export function resolveAdminV2PaywallBranchSlugFromUpgradeReason(
  reason: AdminV2PaywallUpgradeReason
): AdminV2PaywallPopupBranchSlug {
  if (!reason) {
    throw new Error('Paywall upgrade reason is required.');
  }
  return upgradeReasonToBranchSlug[reason];
}

export function isAdminV2PaywallPopupBranchSlug(
  value: string
): value is AdminV2PaywallPopupBranchSlug {
  return adminV2PaywallPopupBranchSlugs.includes(
    value as AdminV2PaywallPopupBranchSlug
  );
}

export function parseAdminV2PaywallPopupBranchSlug(
  value: string
): AdminV2PaywallPopupBranchSlug {
  if (!isAdminV2PaywallPopupBranchSlug(value)) {
    throw new Error(`Unknown paywall popup branch slug: ${value}.`);
  }
  return value;
}
